// ==============================================================================
// REPOSITÓRIO CENTRAL DE EXEMPLOS E CENÁRIOS PARA GRAVAÇÃO DE TUTORIAIS & VÍDEOS
// ==============================================================================
// Este arquivo armazena os cenários completos de demonstração (Salão de Beleza,
// Pet Shop, Oficina Mecânica, Elétrica, etc.) para que possam ser carregados
// sob demanda pelo criador/administrador ao gravar tutoriais e vídeos explicativos.
//
// ⚠️ ATENÇÃO: Nenhum destes dados deve ser injetado compulsoriamente na conta de
// um novo usuário real. Novos usuários devem SEMPRE iniciar com base limpa ([]).
// ==============================================================================

import { 
  StaffMember, 
  SubscriptionClubPlan, 
  CalendarServiceItem, 
  StorefrontProfile, 
  Receipt 
} from '../types';

export interface TutorialVerticalPreset {
  id: string;
  name: string;
  nicheLabel: string;
  profile: StorefrontProfile;
  staff: StaffMember[];
  services: CalendarServiceItem[];
  subscriptionClubs: SubscriptionClubPlan[];
  sampleReceipts?: Partial<Receipt>[];
}

// ────────────────────────────────────────────────────────────────────────────
// 1. CENÁRIO: SALÃO DE BELEZA, CABELO & ESTÉTICA (STUDIO BELLA)
// ────────────────────────────────────────────────────────────────────────────
export const TUTORIAL_DEMO_SALAO: TutorialVerticalPreset = {
  id: 'salao_estetica',
  name: 'Studio Bella • Salão & Estética',
  nicheLabel: 'Salão de Beleza & Estética Feminina',
  profile: {
    id: 'store_salao_demo',
    userId: 'demo_user',
    slug: 'studio-bella',
    displayName: 'Studio Bella • Cabelo & Estética',
    tagline: 'Alongamento em Gel, Mechas, Escova e Estética Facial',
    bio: 'Salão especializado em beleza feminina com equipe certificada, esterilização hospitalar e atendimento com hora marcada.',
    yearsExperience: '8 anos',
    nicheBadges: ['Salão de Beleza', 'Unhas em Gel', 'Mechas & Loiros', 'Estética Facial'],
    whatsappNumber: '11999999999',
    instagramHandle: '@studiobellasp',
    facebookHandle: 'fb.com/studiobellasp',
    tiktokHandle: '@studiobellasp',
    email: 'contato@studiobella.com.br',
    addressDisplay: 'Rua Oscar Freire, 1050 - Jardins, São Paulo - SP',
    accentColor: '#0d9488',
    isPublished: true,
    viewCount: 4120,
    portfolioItems: []
  },
  staff: [
    { 
      id: 'st_demo_mariana', 
      name: 'Mariana Silva', 
      role: 'Manicure & Alongamento em Gel', 
      color: 'rose', 
      commissionPercent: 50, 
      active: true, 
      accessToken: 'demo-mariana' 
    },
    { 
      id: 'st_demo_carla', 
      name: 'Carla Dias', 
      role: 'Cabeleireira & Colorista (Mechas)', 
      color: 'purple', 
      commissionPercent: 60, 
      active: true, 
      accessToken: 'demo-carla' 
    },
    { 
      id: 'st_demo_leticia', 
      name: 'Letícia Souza', 
      role: 'Esteticista & Designer de Sobrancelhas', 
      color: 'amber', 
      commissionPercent: 50, 
      active: true, 
      accessToken: 'demo-leticia' 
    }
  ],
  services: [
    { 
      id: 'cs_demo_alongamento', 
      name: 'Alongamento em Gel / Fibra de Vidro', 
      category: 'Unhas', 
      durationMinutes: 120, 
      price: 160, 
      commissionPercent: 50, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_manicure_pedi', 
      name: 'Manicure + Pedicure Tradicional', 
      category: 'Unhas', 
      durationMinutes: 60, 
      price: 65, 
      commissionPercent: 50, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_blindagem', 
      name: 'Blindagem de Unhas Naturais', 
      category: 'Unhas', 
      durationMinutes: 45, 
      price: 70, 
      commissionPercent: 50, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_corte_escova', 
      name: 'Corte Feminino + Escova Modelada', 
      category: 'Cabelo', 
      durationMinutes: 60, 
      price: 110, 
      commissionPercent: 60, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_mechas', 
      name: 'Mechas & Morena Iluminada com Nutrição', 
      category: 'Cabelo', 
      durationMinutes: 180, 
      price: 380, 
      commissionPercent: 60, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_sobrancelha', 
      name: 'Design de Sobrancelhas com Henna', 
      category: 'Estética', 
      durationMinutes: 30, 
      price: 55, 
      commissionPercent: 50, 
      acceptOnlineBooking: true 
    }
  ],
  subscriptionClubs: [
    {
      id: 'sub_demo_corte_vip',
      name: 'Clube do Corte VIP (Ilimitado)',
      priceMonthly: 119.90,
      description: 'Cortes ilimitados no mês com horário preferencial e café/cerveja cortesia.',
      benefits: ['Cortes de cabelo livres no mês', '1 Bebida especial por visita', '10% de desconto em produtos de balcão'],
      eligibleServices: ['Corte Degradê / Fade', 'Corte Feminino + Escova'],
      activeSubscribersCount: 28
    },
    {
      id: 'sub_demo_combo_barba',
      name: 'Plano Barba & Cabelo Master',
      priceMonthly: 189.90,
      description: 'Cortes e barboterapia livre o mês todo para manter o alinhamento impecável.',
      benefits: ['Cortes e Barboterapia ilimitados', 'Toalha quente com massagem', '15% de desconto em cosméticos'],
      eligibleServices: ['Corte Tradicional', 'Barboterapia com Toalha Quente'],
      activeSubscribersCount: 19
    },
    {
      id: 'sub_demo_unhas_semanal',
      name: 'Assinatura Bella Unhas (4x no Mês)',
      priceMonthly: 149.90,
      description: '1 Atendimento semanal garantido de Manicure e Pedicure com horário fixo reservado.',
      benefits: ['4 Sessões de Manicure/Pedicure no mês', 'Garantia de retoque gratuito em 5 dias', 'Fila prioritária na agenda'],
      eligibleServices: ['Manicure + Pedicure Tradicional'],
      maxServicesPerMonth: 4,
      activeSubscribersCount: 34
    }
  ],
  sampleReceipts: [
    {
      receiptNumber: 'REC-1082',
      totalServiceValue: 650,
      amountPaid: 650,
      remainingBalance: 0,
      paymentMethod: 'pix',
      receiptType: 'full',
      clientName: 'Fernanda Montenegro Silveira',
      clientDocument: '119.882.340-92',
      description: 'Manutenção de Mechas Loiro Pérola + Reconstrução Capilar com Ozônio',
      warrantyPeriod: 30,
      warrantyUnit: 'dias',
      warrantyTerms: 'Garantia de retoque e integridade da fibra capilar em 30 dias.'
    },
    {
      receiptNumber: 'REC-1081',
      totalServiceValue: 380,
      amountPaid: 190,
      remainingBalance: 190,
      paymentMethod: 'pix',
      receiptType: 'deposit',
      clientName: 'Juliana Paes de Camargo',
      clientDocument: '342.109.876-04',
      description: 'Sinal de 50% para Pacote Noiva & Madrinhas',
      warrantyPeriod: 90,
      warrantyUnit: 'dias',
      warrantyTerms: 'Garantia de execução na data reservada.'
    }
  ]
};

// ────────────────────────────────────────────────────────────────────────────
// 2. CENÁRIO: CLÍNICA VETERINÁRIA & PET SHOP
// ────────────────────────────────────────────────────────────────────────────
export const TUTORIAL_DEMO_PET_VET: TutorialVerticalPreset = {
  id: 'pet_vet',
  name: 'Hospital Veterinário & Pet Shop Amigo Fiel',
  nicheLabel: 'Clínica Veterinária, Banho & Tosa',
  profile: {
    id: 'store_pet_demo',
    userId: 'demo_user',
    slug: 'amigo-fiel-vet',
    displayName: 'Clínica & Pet Shop Amigo Fiel',
    tagline: 'Consultas, Vacinas V10/Raiva, Cirurgias e Banho com Ozônio',
    bio: 'Atendimento clínico humanizado para cães e gatos, centro cirúrgico completo e estética pet com banho climatizado.',
    yearsExperience: '10 anos',
    nicheBadges: ['Medicina Veterinária', 'Vacinação Ética', 'Banho & Tosa', 'Cirurgia Geral'],
    whatsappNumber: '11988888888',
    addressDisplay: 'Av. Ibirapuera, 2200 - Moema, São Paulo - SP',
    accentColor: '#0284c7',
    isPublished: true,
    viewCount: 2840,
    portfolioItems: []
  },
  staff: [
    { 
      id: 'st_demo_roberto', 
      name: 'Dr. Roberto Mendonça', 
      role: 'Médico Veterinário (CRMV-SP 42.110)', 
      color: 'blue', 
      commissionPercent: 50, 
      active: true, 
      accessToken: 'demo-roberto' 
    },
    { 
      id: 'st_demo_camila', 
      name: 'Dra. Camila Duarte', 
      role: 'Clínica Geral & Dermatologia Veterinária', 
      color: 'teal', 
      commissionPercent: 50, 
      active: true, 
      accessToken: 'demo-camila' 
    },
    { 
      id: 'st_demo_marcos', 
      name: 'Marcos Lima', 
      role: 'Tosador Chefe & Estética Animal', 
      color: 'purple', 
      commissionPercent: 40, 
      active: true, 
      accessToken: 'demo-marcos' 
    }
  ],
  services: [
    { 
      id: 'cs_demo_consulta_vet', 
      name: 'Consulta Clínica Geral com Anamnese', 
      category: 'Clínica', 
      durationMinutes: 45, 
      price: 180, 
      commissionPercent: 50, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_vacina_v10', 
      name: 'Vacina V10 Importada + Avaliação Física', 
      category: 'Vacinação', 
      durationMinutes: 30, 
      price: 120, 
      commissionPercent: 40, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_banho_tosa', 
      name: 'Banho & Tosa Completa Porte Médio (Hidratação)', 
      category: 'Banho & Tosa', 
      durationMinutes: 90, 
      price: 95, 
      commissionPercent: 40, 
      acceptOnlineBooking: true 
    }
  ],
  subscriptionClubs: [
    {
      id: 'sub_demo_pet_banho',
      name: 'Plano Pet Limpinho (4 Banhos/mês)',
      priceMonthly: 280,
      description: '1 Banho semanal com hidratação, corte de unhas e limpeza de ouvidos.',
      benefits: ['4 Banhos mensais', 'Transporte Táxi Dog com 20% OFF', 'Retoque de tosa higiênica grátis'],
      eligibleServices: ['Banho & Tosa Completa Porte Médio (Hidratação)'],
      activeSubscribersCount: 42
    }
  ]
};

// ────────────────────────────────────────────────────────────────────────────
// 3. CENÁRIO: OFICINA MECÂNICA, FUNILARIA & AUTO CENTER
// ────────────────────────────────────────────────────────────────────────────
export const TUTORIAL_DEMO_MECANICA: TutorialVerticalPreset = {
  id: 'mecanica',
  name: 'Auto Mecânica & Centro Automotivo Precision',
  nicheLabel: 'Oficina Mecânica & Auto Center',
  profile: {
    id: 'store_mecanica_demo',
    userId: 'demo_user',
    slug: 'precision-auto-center',
    displayName: 'Precision Auto Center',
    tagline: 'Injeção Eletrônica, Freios ABS, Suspensão e Revisão Preventiva',
    bio: 'Oficina multimarcas com scanner de diagnóstico computadorizado, ferramentas de torque e técnicos certificados ASE.',
    yearsExperience: '14 anos',
    nicheBadges: ['Injeção Eletrônica', 'Freios e Suspensão', 'Troca de Óleo', 'Revisão Preventiva'],
    whatsappNumber: '11977777777',
    addressDisplay: 'Av. Santo Amaro, 4500 - Brooklin, São Paulo - SP',
    accentColor: '#d97706',
    isPublished: true,
    viewCount: 1950,
    portfolioItems: []
  },
  staff: [
    { 
      id: 'st_demo_carlos', 
      name: 'Carlos Eduardo', 
      role: 'Mecânico Chefe & Diagnóstico Eletrônico', 
      color: 'amber', 
      commissionPercent: 45, 
      active: true, 
      accessToken: 'demo-carlos' 
    },
    { 
      id: 'st_demo_lucas', 
      name: 'Lucas Mendes', 
      role: 'Técnico de Freios, Suspensão e Alinhamento', 
      color: 'blue', 
      commissionPercent: 40, 
      active: true, 
      accessToken: 'demo-lucas' 
    },
    { 
      id: 'st_demo_rodrigo', 
      name: 'Rodrigo Silva', 
      role: 'Perito Vistoriador & Check-up Pré-Compra', 
      color: 'purple', 
      commissionPercent: 35, 
      active: true, 
      accessToken: 'demo-rodrigo' 
    }
  ],
  services: [
    { 
      id: 'cs_demo_revisao_freios', 
      name: 'Revisão do Sistema de Freios (Pastilhas + Discos + Fluido)', 
      category: 'Freios & Segurança', 
      durationMinutes: 90, 
      price: 280, 
      commissionPercent: 40, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_scanner', 
      name: 'Diagnóstico Computadorizado com Scanner OBD-II', 
      category: 'Injeção & Motor', 
      durationMinutes: 45, 
      price: 150, 
      commissionPercent: 50, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_troca_oleo', 
      name: 'Troca de Óleo Sintético + 4 Filtros (Ar/Óleo/Combustível/Cabine)', 
      category: 'Manutenção Rápida', 
      durationMinutes: 60, 
      price: 340, 
      commissionPercent: 30, 
      acceptOnlineBooking: true 
    }
  ],
  subscriptionClubs: [
    {
      id: 'sub_demo_auto_frota',
      name: 'Plano Frotista / Motorista de App Seguro',
      priceMonthly: 199,
      description: 'Manutenção preventiva mensal com check-up de 30 itens e desconto prioritário.',
      benefits: ['Check-up mensal de suspensão e freios', 'Alinhamento e balanceamento 3D trimestral', 'Socorro guincho em raio de 15km'],
      eligibleServices: ['Diagnóstico Computadorizado com Scanner OBD-II'],
      activeSubscribersCount: 15
    }
  ]
};

// ────────────────────────────────────────────────────────────────────────────
// 4. CENÁRIO: ELETRICISTA & INSTALAÇÕES ELÉTRICAS
// ────────────────────────────────────────────────────────────────────────────
export const TUTORIAL_DEMO_ELETRICA: TutorialVerticalPreset = {
  id: 'eletrica',
  name: 'Voltsul Engenharia & Instalações Elétricas',
  nicheLabel: 'Eletricista Residencial, Comercial & Predial',
  profile: {
    id: 'store_eletrica_demo',
    userId: 'demo_user',
    slug: 'voltsul-eletrica',
    displayName: 'Voltsul Soluções Elétricas',
    tagline: 'Quadros de Distribuição, Padrão Enel, IDR/DPS e Laudos NR-10',
    bio: 'Equipe de eletricistas credenciados pelo CFT/CREA com equipamentos calibrados e cumprimento rigoroso da NBR 5410.',
    yearsExperience: '12 anos',
    nicheBadges: ['Eletricista Certificado', 'Quadro QDT', 'Proteção contra Raios', 'Laudo Técnico'],
    whatsappNumber: '11966666666',
    addressDisplay: 'São Paulo e Grande SP (Atendimento no Local)',
    accentColor: '#2563eb',
    isPublished: true,
    viewCount: 1420,
    portfolioItems: []
  },
  staff: [
    { 
      id: 'st_demo_fabio', 
      name: 'Fábio Eletricista', 
      role: 'Eletricista Chefe (NR-10 & SEP)', 
      color: 'amber', 
      commissionPercent: 50, 
      active: true, 
      accessToken: 'demo-fabio' 
    },
    { 
      id: 'st_demo_ajudante', 
      name: 'Gabriel Martins', 
      role: 'Técnico de Campo & Passagem de Cabos', 
      color: 'blue', 
      commissionPercent: 35, 
      active: true, 
      accessToken: 'demo-gabriel' 
    }
  ],
  services: [
    { 
      id: 'cs_demo_troca_qdt', 
      name: 'Montagem e Revisão de Quadro QDT com Barramento DIN', 
      category: 'Quadros & Proteção', 
      durationMinutes: 180, 
      price: 450, 
      commissionPercent: 50, 
      acceptOnlineBooking: true 
    },
    { 
      id: 'cs_demo_instalacao_chuveiro', 
      name: 'Instalação de Chuveiro Elétrico com Circuito Dedicado', 
      category: 'Residencial', 
      durationMinutes: 60, 
      price: 180, 
      commissionPercent: 50, 
      acceptOnlineBooking: true 
    }
  ],
  subscriptionClubs: []
};

// ────────────────────────────────────────────────────────────────────────────
// DICIONÁRIO COMPLETO DE PRESETS DE TUTORIAL
// ────────────────────────────────────────────────────────────────────────────
export const ALL_TUTORIAL_PRESETS: Record<string, TutorialVerticalPreset> = {
  salao: TUTORIAL_DEMO_SALAO,
  estetica: TUTORIAL_DEMO_SALAO,
  pet: TUTORIAL_DEMO_PET_VET,
  pet_vet: TUTORIAL_DEMO_PET_VET,
  mecanica: TUTORIAL_DEMO_MECANICA,
  technical_service: TUTORIAL_DEMO_MECANICA,
  eletrica: TUTORIAL_DEMO_ELETRICA
};

/**
 * Retorna o preset de tutorial correspondente a uma chave de nicho/vertical
 */
export function getTutorialPreset(key?: string): TutorialVerticalPreset {
  if (!key) return TUTORIAL_DEMO_SALAO;
  const k = key.toLowerCase();
  if (k.includes('pet') || k.includes('vet') || k.includes('cao') || k.includes('gato')) return TUTORIAL_DEMO_PET_VET;
  if (k.includes('mecan') || k.includes('auto') || k.includes('carro') || k.includes('oficina')) return TUTORIAL_DEMO_MECANICA;
  if (k.includes('eletric') || k.includes('energia')) return TUTORIAL_DEMO_ELETRICA;
  return ALL_TUTORIAL_PRESETS[k] || TUTORIAL_DEMO_SALAO;
}
